// frontend/src/App.jsx
// Wires AuthGate (Cognito) around the TrackFin app.
// All data now comes from DynamoDB via API Gateway + Lambda.

import { useState, useEffect, useCallback } from "react";
import { fetchUserAttributes, updateUserAttribute } from "aws-amplify/auth";
import "./App.css";
import { AuthGate } from "./auth/AuthScreens";
import { transactionsApi, aiApi, profileApi } from "./api/client";
import InvestmentsScreen from "./screens/InvestmentsScreen";
import FamilyScreen from "./screens/FamilyScreen";
import RoadmapScreen from "./screens/RoadmapScreen";
import PendingInbox from "./screens/PendingInbox";
import { ESSENTIAL_CATEGORIES, TagInput } from "./screens/theme";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { TabIcon } from "./assets/TabIcons";
import { ReceiptQueueProvider } from "./screens/ReceiptQueueContext";

// LinkedIn-style light theme — keep in sync with src/screens/theme.js
const C = {
  bg: "#f4f2ee", surface: "#f3f2f0", card: "#ffffff",
  border: "rgba(0,0,0,0.08)",
  accent: "#1565d8", accentDim: "#1565d815", accentDeep: "#0d4ba0", black: "#111111",
  gold: "#915907",
  red: "#cc1016", blue: "#0a66c2", purple: "#7a3edb",
  text: "rgba(0,0,0,0.9)", muted: "rgba(0,0,0,0.6)", success: "#057642",
  greyLight: "rgba(0,0,0,0.06)", greyHover: "rgba(0,0,0,0.1)",
  greyDark: "rgba(0,0,0,0.85)", greyOnDark: "#ffffff",
};

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

// ── Main App shell ────────────────────────────────────────────────────────────
function TrackerApp({ onSignOut }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Default to ALL months so historical transactions are visible on load.
  // Earlier this defaulted to the current month, which hid past data on the 1st of a new month.
  const [filter, setFilter] = useState({ month: "" });
  const [form, setForm] = useState({ type: "expense", category: "Food", amount: "", member: "Self", txnDate: new Date().toISOString().slice(0, 10), note: "", tags: [] });
  const [aiText, setAiText] = useState("");
  const [aiError, setAiError] = useState(null);   // { code, message }
  const [aiLoading, setAiLoading] = useState(false);
  const [aiUsage, setAiUsage] = useState(null);   // { used, limit }

  useEffect(() => {
    aiApi.getUsage().then(setAiUsage).catch(() => {});
  }, []);
  const [tab, setTab] = useState("dashboard");
  const [userInfo, setUserInfo] = useState(null);
  const [showUserPanel, setShowUserPanel] = useState(false);
  const [includeTransfers, setIncludeTransfers] = useState(false);
  const [holdings, setHoldings] = useState([]);
  const [orFilter, setOrFilter] = useState({ bank: "", member: "", paymentMethod: "", category: "" });
  const [showAddTxn, setShowAddTxn] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft]     = useState("");
  const [savingName, setSavingName]   = useState(false);

  const saveName = async () => {
    const value = nameDraft.trim();
    if (!value) return;
    setSavingName(true);
    try {
      await updateUserAttribute({ userAttribute: { attributeKey: "name", value } });
      const fresh = await fetchUserAttributes();
      setUserInfo(fresh);
      setEditingName(false);
    } catch (e) {
      setError(e.message || "Could not save name");
    } finally {
      setSavingName(false);
    }
  };

  useEffect(() => {
    profileApi.getHoldings().then(d => setHoldings(d.holdings || [])).catch(() => {});
  }, []);

  const ALL_ROLES = ["Self", "Spouse", "Child 1", "Child 2", "Parent 1", "Parent 2"];
  const [familyMembers, setFamilyMembers] = useState(ALL_ROLES.map(r => ({ role: r, name: r })));
  const [editMembers, setEditMembers]     = useState(null);
  const [savingFamily, setSavingFamily]   = useState(false);
  const [invite, setInvite]               = useState(null);   // {spouseEmail, inviteCode}
  const [link, setLink]                   = useState(null);   // {linkedUserId}
  const [inviteEmail, setInviteEmail]     = useState("");
  const [joinCode, setJoinCode]           = useState("");
  const [sharingBusy, setSharingBusy]     = useState(false);

  useEffect(() => {
    fetchUserAttributes().then(setUserInfo).catch(() => {});
    profileApi.getFamily()
      .then(data => {
        setFamilyMembers(data.members);
        setEditMembers(data.members.map(m => ({ ...m })));
        setInvite(data.invite || null);
        setLink(data.link || null);
        // If the logged-in user is the spouse (they joined someone else's family),
        // default the Add Transaction Member picker to "Spouse" instead of "Self".
        if (data.link) setForm(f => ({ ...f, member: "Spouse" }));
      })
      .catch(() => { setEditMembers(ALL_ROLES.map(r => ({ role: r, name: r }))); });
  }, []);

  // ── Load transactions from DynamoDB ──────────────────────────────────────
  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await transactionsApi.list({ month: filter.month });
      setTransactions(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter.month]);

  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  // ── Add transaction ───────────────────────────────────────────────────────
  const addTransaction = async () => {
    if (!form.amount) return;
    try {
      await transactionsApi.create({ ...form, amount: Number(form.amount), tags: form.tags });
      setForm(f => ({ ...f, amount: "", note: "", tags: [] }));
      loadTransactions();
    } catch (err) {
      setError(err.message);
    }
  };

  // ── Delete transaction ────────────────────────────────────────────────────
  const deleteTransaction = async (id, txnDate) => {
    try {
      await transactionsApi.delete(id, txnDate);
      setTransactions(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  // ── AI Analysis ───────────────────────────────────────────────────────────
  const runAI = async (analysisType) => {
    setAiLoading(true); setAiText(""); setAiError(null); setTab("insights");
    const income = transactions.filter(t => t.type === "income").reduce((s, t) => s + t.amount, 0);
    const expense = transactions.filter(t => t.type === "expense").reduce((s, t) => s + t.amount, 0);
    const byCategory = {};
    transactions.filter(t => t.type === "expense").forEach(t => {
      byCategory[t.category] = (byCategory[t.category] || 0) + t.amount;
    });
    try {
      const result = await aiApi.analyze(analysisType, {
        totalIncome: income, totalExpense: expense, savings: income - expense,
        savingsRate: income ? Math.round((income - expense) / income * 100) : 0,
        expenseBreakdown: Object.entries(byCategory).map(([category, amount]) => ({ category, amount })),
        riskProfile: "moderate", age: 35, goal: "Financial freedom",
        hasSpouse: true, hasKids: true, hasParents: true,
      });
      setAiText(result.analysis);
      if (result.monthlyUsage) setAiUsage(result.monthlyUsage);
    } catch (err) {
      setAiError({ code: err.code || "AI_UNAVAILABLE", message: err.message });
      // Refresh usage so the badge reflects the latest count
      aiApi.getUsage().then(setAiUsage).catch(() => {});
    } finally {
      setAiLoading(false);
    }
  };

  const isTransfer  = (t) => Array.isArray(t.tags) && t.tags.includes("transfer");
  const countable   = (t) => includeTransfers || !isTransfer(t);
  // Per-transaction override beats the category default
  const isEssential = (t) => typeof t.isEssential === "boolean"
    ? t.isEssential
    : ESSENTIAL_CATEGORIES.has(t.category);

  const totalIncome      = transactions.filter(t => t.type === "income"  && countable(t)).reduce((s, t) => s + t.amount, 0);
  const essentialExpense = transactions
    .filter(t => t.type === "expense" && countable(t) && isEssential(t))
    .reduce((s, t) => s + t.amount, 0);

  const toggleEssential = async (t) => {
    const current = isEssential(t);
    try {
      await transactionsApi.setEssential(t.id, t.txnDate, !current);
      setTransactions(prev => prev.map(x => x.id === t.id ? { ...x, isEssential: !current } : x));
    } catch (e) { setError(e.message); }
  };
  const totalExpense = transactions.filter(t => t.type === "expense" && countable(t)).reduce((s, t) => s + t.amount, 0);
  const transferCount = transactions.filter(isTransfer).length;
  const savings = totalIncome - totalExpense;

  const tabs = [
    { id: "dashboard",    label: "Home",        icon: "home",    short: "Home" },
    { id: "transactions", label: "Transactions", icon: "txn",     short: "Txns" },
    { id: "investments",  label: "Investments", icon: "invest",  short: "Invest" },
    { id: "family",       label: "Family",      icon: "family",  short: "Family" },
    { id: "insights",     label: "AI Insights", icon: "ai",      short: "AI" },
    { id: "roadmap",      label: "Roadmap",     icon: "roadmap", short: "Roadmap" },
  ];

  const CATEGORIES = {
    income: ["Salary", "Business", "Freelance", "Rent", "Dividends", "Interest Earned", "Other Income"],
    expense: ["Rent", "Housing", "Food", "Transport", "Healthcare", "Education", "Entertainment", "Utilities", "EMI/Loan", "Insurance", "Clothing", "Personal Care", "Investments", "Donation", "Other"],
  };
  const memberNameMap   = Object.fromEntries(familyMembers.map(m => [m.role, m.name || m.role]));
  const activeMembers   = familyMembers.filter(m => m.name && m.name.trim());
  const selfMember      = familyMembers.find(m => m.role === "Self");
  const selfName        = selfMember?.name && selfMember.name.trim() !== "Self" ? selfMember.name.trim() : "";
  const displayName     = userInfo?.name?.trim() || selfName;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>
      {/* Header */}
      <div className="app-header" style={{ background: C.card }}>
        <div className="app-header-inner">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="36" height="36" viewBox="0 0 100 100" aria-hidden="true">
              <rect width="100" height="100" rx="8" fill="#1565d8"/>
              <g fill="none" stroke="#ffffff" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M 40 14 C 37 18, 34 24, 33 32 L 28 76 C 27 84, 33 88, 44 82"/>
                <path d="M 66 30 C 70 10, 56 6, 52 14 C 48 20, 50 28, 54 30 L 70 76 C 72 84, 80 87, 76 82"/>
                <path d="M 16 44 C 24 41, 36 41, 44 44"/>
                <path d="M 46 52 C 56 49, 70 49, 80 52"/>
              </g>
            </svg>
            <div>
              <div style={{ fontWeight: 600, fontSize: 18, letterSpacing: "-0.3px", lineHeight: 1.1 }}>
                <span style={{ color: C.text }}>Track</span><span style={{ fontWeight: 400, color: C.accent }}>Fin</span>
              </div>
              <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.6px", marginTop: 1 }}>YOUR MONEY. YOUR PEACE.</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div className="header-tabs">
              {tabs.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className="header-tab-item"
                  style={{
                    color: tab === t.id ? C.text : C.muted,
                    borderBottom: tab === t.id ? `2px solid ${C.text}` : "2px solid transparent",
                    fontWeight: tab === t.id ? 700 : 500,
                  }}>
                  <span className="header-tab-icon"><TabIcon name={t.icon} size={22} /></span>
                  <span className="header-tab-label">{t.label}</span>
                </button>
              ))}
            </div>
            <div style={{ position: "relative" }}>
              <button onClick={() => setShowUserPanel(p => !p)}
                style={{
                  background: "transparent",
                  border: "none",
                  borderRadius: 24,
                  padding: "4px 12px 4px 4px",
                  color: C.muted,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}>
                <span style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${C.accent}, ${C.accentDeep || "#0d4ba0"})`,
                  color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 13,
                }}>
                  {(displayName || userInfo?.email || "U").charAt(0).toUpperCase()}
                </span>
                <span style={{ color: C.text, display: "flex", alignItems: "center", gap: 4 }}>
                  {displayName || userInfo?.email?.split("@")[0] || "Account"}
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"
                       style={{ transition: "transform 0.15s", transform: showUserPanel ? "rotate(180deg)" : "rotate(0deg)" }}>
                    <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </span>
              </button>
              {showUserPanel && (
                <div className="account-dropdown" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, fontWeight: 700, letterSpacing: 0.5 }}>ACCOUNT</div>
                  {editingName ? (
                    <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                      <input
                        autoFocus
                        value={nameDraft}
                        onChange={e => setNameDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                        placeholder="Your name"
                        style={{ flex: 1, padding: "6px 10px", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: "inherit", fontSize: 13 }} />
                      <button onClick={saveName} disabled={savingName}
                        style={{ padding: "4px 10px", background: C.accent, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                        {savingName ? "..." : "Save"}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 13, color: C.text, flex: 1 }}>{userInfo?.name || "—"}</div>
                      <button onClick={() => { setNameDraft(userInfo?.name || ""); setEditingName(true); }}
                        style={{ background: "transparent", border: "none", color: C.blue, cursor: "pointer", fontSize: 11, fontWeight: 600, padding: 0 }}>
                        {userInfo?.name ? "Edit" : "Add name"}
                      </button>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{userInfo?.email}</div>
                  <div style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, display: "inline-block", marginBottom: 12,
                    background: userInfo?.email_verified === "true" ? C.success + "22" : C.gold + "22",
                    color: userInfo?.email_verified === "true" ? C.success : C.gold }}>
                    {userInfo?.email_verified === "true" ? "✓ Email verified" : "⚠ Email not verified"}
                  </div>
                  <button onClick={onSignOut}
                    style={{ width: "100%", padding: "7px", background: C.red + "22", border: `1px solid ${C.red}44`, borderRadius: 8, color: C.red, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="app-content">
        {error && <div style={{ background: C.red + "22", border: `1px solid ${C.red}44`, borderRadius: 10, padding: "12px 16px", color: C.red, marginBottom: 16, fontSize: 14 }}>{error} <button onClick={() => setError("")} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", marginLeft: 8 }}>✕</button></div>}

        {/* DASHBOARD */}
        {tab === "dashboard" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "flex", gap: 12 }}>
              <input type="month" value={filter.month} onChange={e => setFilter({ month: e.target.value })}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 14px", color: C.text, fontSize: 13, fontFamily: "inherit" }} />
            </div>
            <div className="dashboard-grid">
              {[
                { label: "Total Income", value: fmt(totalIncome), color: C.success, icon: "📥" },
                { label: "Total Expenses", value: fmt(totalExpense), color: C.red, icon: "📤" },
                { label: "Net Savings", value: fmt(savings), color: savings >= 0 ? C.accent : C.red, icon: "💰" },
                { label: "Transactions", value: loading ? "..." : transactions.length, color: C.blue, icon: "📋" },
              ].map(k => (
                <div key={k.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
                  <div style={{ fontSize: 20 }}>{k.icon}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: k.color, marginTop: 8 }}>{k.value}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{k.label}</div>
                </div>
              ))}
            </div>
            {/* Holdings + Expenses widgets — side by side on desktop, stacked on mobile */}
            {(holdings.length > 0 || totalExpense > 0) && (() => {
              const colors = [C.accent, C.blue, C.gold, C.purple, C.red, C.success, "#f97316", "#94a3b8", "#fbbf24", "#f472b6", "#60a5fa"];

              // Holdings pie data
              const totalShares = holdings.reduce((s, h) => s + h.quantity, 0);
              const sectorMap = {};
              for (const h of holdings) {
                const sec = h.sector || "Other";
                sectorMap[sec] = (sectorMap[sec] || 0) + h.quantity;
              }
              const holdingsData = Object.entries(sectorMap)
                .sort((a, b) => b[1] - a[1])
                .map(([name, value]) => ({ name, value }));

              // Expenses pie data — category breakdown for the filtered month
              const expenseMap = {};
              for (const t of transactions) {
                if (t.type !== "expense" || !countable(t)) continue;
                const cat = t.category || "Other";
                expenseMap[cat] = (expenseMap[cat] || 0) + (t.amount || 0);
              }
              const expensesData = Object.entries(expenseMap)
                .sort((a, b) => b[1] - a[1])
                .map(([name, value]) => ({ name, value }));

              const DonutCard = ({ title, sub, data, total, formatValue, formatLegend, footer }) => (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{sub}</div>
                  </div>
                  <div className="donut-row" style={{ gridTemplateColumns: "160px 1fr" }}>
                    <div style={{ height: 160 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                            {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                          </Pie>
                          <RTooltip formatter={formatValue} contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflowY: "auto" }}>
                      {data.map((d, i) => (
                        <div key={d.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: `1px solid ${C.border}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: colors[i % colors.length], flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: C.text, flexShrink: 0 }}>{formatLegend(d.value, total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {footer && <div style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>{footer}</div>}
                </div>
              );

              return (
                <div className="two-col-grid">
                  {holdings.length > 0 && (
                    <DonutCard
                      title="📊 Your Stock Holdings"
                      sub={`${holdings.length} stocks · ${totalShares.toFixed(0)} shares`}
                      data={holdingsData}
                      total={totalShares}
                      formatValue={(v, n) => [`${v} shares`, n]}
                      formatLegend={(v, t) => `${Math.round((v / t) * 100)}%`}
                      footer="By share count · See Investments tab for editing or value-weighted view"
                    />
                  )}
                  {totalExpense > 0 && (
                    <DonutCard
                      title="💸 Expense Allocation"
                      sub={`${expensesData.length} categories · ${fmt(totalExpense)}`}
                      data={expensesData}
                      total={totalExpense}
                      formatValue={(v, n) => [fmt(v), n]}
                      formatLegend={(v, t) => `${Math.round((v / t) * 100)}%`}
                      footer={filter.month ? `For ${filter.month}` : "All months"}
                    />
                  )}
                </div>
              );
            })()}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>🤖 AI-Powered Analysis</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Personalized guidance based on your actual transactions</div>
              <div className="ai-row">
                <button onClick={() => runAI("overall")} className="pill-outline-btn" style={{ flex: 1, minWidth: 150 }}>📊 Overall Review</button>
                <button onClick={() => runAI("invest")} className="pill-outline-btn" style={{ flex: 1, minWidth: 180 }}>📈 Investment Plan</button>
                <button onClick={() => runAI("family")} className="pill-outline-btn" style={{ flex: 1, minWidth: 180 }}>👨‍👩‍👧 Family Plan</button>
              </div>
            </div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 16, fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
              🔒 <strong style={{ color: C.text }}>Data Policy:</strong> Raw transactions are auto-deleted after 45 days. Category summaries are retained permanently per IT Act 2000 standards for financial analysis. You can request deletion of all category data from Settings.
            </div>
          </div>
        )}

        {/* TRANSACTIONS */}
        {tab === "transactions" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <PendingInbox familyMembers={familyMembers} onConfirmed={loadTransactions} />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowAddTxn(s => !s)} className="pill-outline-btn">
                {showAddTxn ? "Cancel" : "+ Add Transaction"}
              </button>
            </div>
            {showAddTxn && (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
              <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 14 }}>➕ Add Transaction</div>
              <div className="form-grid">
                {[
                  { label: "Type", field: "type", options: ["income", "expense"] },
                  { label: "Category", field: "category", options: CATEGORIES[form.type] },
                ].map(({ label, field, options }) => (
                  <div key={field} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <label style={{ color: C.muted, fontSize: 12, fontWeight: 600 }}>{label}</label>
                    <select value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value, ...(field === "type" ? { category: CATEGORIES[e.target.value][0] } : {}) }))}
                      style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", color: C.text, fontSize: 14, fontFamily: "inherit" }}>
                      {options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ color: C.muted, fontSize: 12, fontWeight: 600 }}>Member</label>
                  <select value={form.member} onChange={e => setForm(f => ({ ...f, member: e.target.value }))}
                    style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", color: C.text, fontSize: 14, fontFamily: "inherit" }}>
                    {activeMembers.map(m => <option key={m.role} value={m.role}>{m.name}</option>)}
                  </select>
                </div>
                {[
                  { label: "Amount (₹)", field: "amount", type: "number", placeholder: "0" },
                  { label: "Date", field: "txnDate", type: "date" },
                  { label: "Note", field: "note", type: "text", placeholder: "Optional" },
                ].map(({ label, field, type, placeholder }) => (
                  <div key={field} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <label style={{ color: C.muted, fontSize: 12, fontWeight: 600 }}>{label}</label>
                    <input type={type} value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} placeholder={placeholder}
                      style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", color: C.text, fontSize: 14, fontFamily: "inherit" }} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={{ color: C.muted, fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>Tags (optional)</label>
                <TagInput tags={form.tags || []} onChange={tags => setForm(f => ({ ...f, tags }))}
                  placeholder="e.g. business, vacation, reimbursable" />
              </div>
              <button onClick={async () => { await addTransaction(); setShowAddTxn(false); }}
                className="pill-outline-btn" style={{ marginTop: 16 }}>
                Add Transaction
              </button>
            </div>
            )}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden" }}>
              <div style={{ padding: "16px 24px", borderBottom: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14 }}>Recent Transactions ({transactions.length}) — auto-deleted after 45 days</div>
              {loading ? <div style={{ padding: 32, textAlign: "center", color: C.muted }}>Loading...</div> : (
                <div style={{ maxHeight: 500, overflowY: "auto" }}>
                  {transactions.sort((a, b) => b.txnDate?.localeCompare(a.txnDate)).map(t => (
                    <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 24px", borderBottom: `1px solid ${C.border}22` }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: (t.type === "income" ? C.success : C.red) + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                        {t.type === "income" ? "📥" : "📤"}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {t.category}
                          {t.paymentMethod === "credit_card" && <span style={{ fontSize: 10, padding: "1px 6px", background: C.purple + "22", color: C.purple, border: `1px solid ${C.purple}44`, borderRadius: 8, fontWeight: 700 }}>💳 CC</span>}
                          {t.bank && <span style={{ fontSize: 10, color: C.muted, fontWeight: 500 }}>{t.bank}</span>}
                          {t.type === "expense" && (() => {
                            const ess = isEssential(t);
                            const color = ess ? C.accent : C.gold;
                            const label = ess ? "✓ essential" : "⚪ discretionary";
                            return (
                              <button onClick={(e) => { e.stopPropagation(); toggleEssential(t); }}
                                title="Click to toggle"
                                style={{ fontSize: 10, padding: "1px 8px", background: color + "22", color, border: `1px solid ${color}44`, borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
                                {label}
                              </button>
                            );
                          })()}
                          {Array.isArray(t.tags) && t.tags.map(tag => {
                            const isTransferTag = tag === "transfer";
                            const tagColor = isTransferTag ? C.blue : C.accent;
                            return (
                              <span key={tag} style={{ fontSize: 10, padding: "1px 8px", background: tagColor + "22", color: tagColor, border: `1px solid ${tagColor}44`, borderRadius: 8, fontWeight: 600 }}>
                                {isTransferTag ? "🔄 transfer" : `#${tag}`}
                              </span>
                            );
                          })}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted }}>{t.note || "—"} · {memberNameMap[t.member] || t.member} · {t.txnDate}</div>
                      </div>
                      <div style={{ fontWeight: 800, color: t.type === "income" ? C.success : C.red }}>{t.type === "income" ? "+" : "-"}{fmt(t.amount)}</div>
                      <button onClick={() => deleteTransaction(t.id, t.txnDate)} style={{ background: C.red + "22", color: C.red, border: `1px solid ${C.red}44`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI INSIGHTS */}
        {tab === "insights" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {aiUsage && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, flexWrap: "wrap", gap: 8 }}>
                <div style={{ fontSize: 13, color: C.muted }}>
                  <strong style={{ color: aiUsage.used >= aiUsage.limit ? C.red : C.text }}>{aiUsage.used} / {aiUsage.limit}</strong> AI analyses used this month
                  {aiUsage.used >= aiUsage.limit && <span style={{ color: C.red, marginLeft: 8 }}>· Free quota reached</span>}
                </div>
                {aiUsage.used >= aiUsage.limit && (
                  <div style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>
                    Pro tier (unlimited) coming soon
                  </div>
                )}
              </div>
            )}

            {/* Overall Review — category breakdown without using AI quota */}
            {(() => {
              const filteredTxns = transactions.filter(t => {
                if (!countable(t)) return false;
                if (orFilter.bank && t.bank !== orFilter.bank) return false;
                if (orFilter.member && t.member !== orFilter.member) return false;
                if (orFilter.paymentMethod) {
                  const pm = t.paymentMethod === "credit_card" ? "credit_card" : "bank";
                  if (pm !== orFilter.paymentMethod) return false;
                }
                if (orFilter.category && t.category !== orFilter.category) return false;
                return true;
              });
              const incomeCats = {};
              const expenseCats = {};
              const paymentTotals = { bank: 0, credit_card: 0 };
              for (const t of filteredTxns) {
                const cat = t.category || "Other";
                const amt = t.amount || 0;
                if (t.type === "income")  incomeCats[cat]  = (incomeCats[cat]  || 0) + amt;
                if (t.type === "expense") {
                  expenseCats[cat] = (expenseCats[cat] || 0) + amt;
                  const pm = t.paymentMethod === "credit_card" ? "credit_card" : "bank";
                  paymentTotals[pm] += amt;
                }
              }
              const uniqueBanks = [...new Set(transactions.map(t => t.bank).filter(Boolean))];
              const uniqueCats  = [...new Set(transactions.map(t => t.category).filter(Boolean))];
              const incomeRows  = Object.entries(incomeCats).sort((a, b) => b[1] - a[1]);
              const expenseRows = Object.entries(expenseCats).sort((a, b) => b[1] - a[1]);
              const totalIncomeSum  = incomeRows.reduce((s, [, v]) => s + v, 0);
              const totalExpenseSum = expenseRows.reduce((s, [, v]) => s + v, 0);

              return (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>📊 Overall Review (this month)</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      {transferCount > 0 && (
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, cursor: "pointer" }}>
                          <input type="checkbox" checked={includeTransfers}
                            onChange={e => setIncludeTransfers(e.target.checked)} />
                          Include {transferCount} transfer{transferCount > 1 ? "s" : ""}
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Filters */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
                    <select value={orFilter.bank} onChange={e => setOrFilter(f => ({ ...f, bank: e.target.value }))}
                      style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: C.text }}>
                      <option value="">All banks</option>
                      {uniqueBanks.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <select value={orFilter.category} onChange={e => setOrFilter(f => ({ ...f, category: e.target.value }))}
                      style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: C.text }}>
                      <option value="">All categories</option>
                      {uniqueCats.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={orFilter.member} onChange={e => setOrFilter(f => ({ ...f, member: e.target.value }))}
                      style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: C.text }}>
                      <option value="">All members</option>
                      {activeMembers.map(m => <option key={m.role} value={m.role}>{m.name}</option>)}
                    </select>
                    <select value={orFilter.paymentMethod} onChange={e => setOrFilter(f => ({ ...f, paymentMethod: e.target.value }))}
                      style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 12, color: C.text }}>
                      <option value="">All payment</option>
                      <option value="bank">Bank/UPI</option>
                      <option value="credit_card">Credit Card</option>
                    </select>
                    {(orFilter.bank || orFilter.member || orFilter.paymentMethod || orFilter.category) && (
                      <button onClick={() => setOrFilter({ bank: "", member: "", paymentMethod: "", category: "" })}
                        style={{ background: C.greyLight, border: "none", color: C.text, borderRadius: 24, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Payment method split */}
                  {(paymentTotals.bank + paymentTotals.credit_card) > 0 && (
                    <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 140, padding: 10, background: C.surface, borderRadius: 8 }}>
                        <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 0.5 }}>BANK / UPI</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginTop: 4 }}>{fmt(paymentTotals.bank)}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 140, padding: 10, background: C.surface, borderRadius: 8 }}>
                        <div style={{ fontSize: 10, color: C.purple, fontWeight: 700, letterSpacing: 0.5 }}>💳 CREDIT CARD</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: C.purple, marginTop: 4 }}>{fmt(paymentTotals.credit_card)}</div>
                      </div>
                    </div>
                  )}

                  <div className="two-col-grid">
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.success, marginBottom: 8, letterSpacing: 0.5 }}>INCOME · {fmt(totalIncomeSum)}</div>
                      {incomeRows.length === 0 ? (
                        <div style={{ fontSize: 12, color: C.muted, padding: "8px 0" }}>No income this month</div>
                      ) : (
                        incomeRows.map(([cat, amt]) => (
                          <div key={cat} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                            <span style={{ color: C.text }}>{cat}</span>
                            <span style={{ color: C.success, fontWeight: 600 }}>{fmt(amt)} · {Math.round((amt / totalIncomeSum) * 100)}%</span>
                          </div>
                        ))
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.red, marginBottom: 8, letterSpacing: 0.5 }}>EXPENSE · {fmt(totalExpenseSum)}</div>
                      {expenseRows.length === 0 ? (
                        <div style={{ fontSize: 12, color: C.muted, padding: "8px 0" }}>No expenses this month</div>
                      ) : (
                        expenseRows.map(([cat, amt]) => (
                          <div key={cat} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                            <span style={{ color: C.text }}>{cat}</span>
                            <span style={{ color: C.red, fontWeight: 600 }}>{fmt(amt)} · {Math.round((amt / totalExpenseSum) * 100)}%</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 16, padding: 12, background: (totalIncomeSum - totalExpenseSum) >= 0 ? C.accentDim : C.red + "15", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: C.muted, fontWeight: 700, letterSpacing: 0.5 }}>NET SAVINGS</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: (totalIncomeSum - totalExpenseSum) >= 0 ? C.accent : C.red }}>
                      {fmt(totalIncomeSum - totalExpenseSum)}
                    </span>
                  </div>
                </div>
              );
            })()}

            <div style={{ fontSize: 12, color: C.muted, fontWeight: 700, letterSpacing: 0.5, marginTop: 4 }}>WANT DEEPER INSIGHTS? RUN AI ANALYSIS:</div>
            <div className="ai-row">
              <button onClick={() => runAI("overall")} className="pill-outline-btn">📊 Overall Review</button>
              <button onClick={() => runAI("invest")} className="pill-outline-btn">📈 Investment Plan</button>
              <button onClick={() => runAI("family")} className="pill-outline-btn">👨‍👩‍👧 Family Plan</button>
            </div>
            {aiLoading && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 40, display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 36, height: 36, border: `3px solid ${C.accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                <div style={{ fontWeight: 700 }}>Analysing your finances with Claude AI...</div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            {aiError && !aiLoading && (
              <div style={{ background: C.card, border: `1px solid ${C.gold}44`, borderRadius: 16, padding: 28 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 36, height: 36, background: C.gold + "22", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚠️</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: C.gold }}>
                    {aiError.code === "AI_MONTHLY_LIMIT"   ? "Monthly Free Limit Reached" :
                     aiError.code === "AI_QUOTA_EXHAUSTED" ? "AI Insights Temporarily Unavailable" :
                     aiError.code === "AI_RATE_LIMITED"    ? "Too Many Requests" :
                     aiError.code === "AI_AUTH_ERROR"      ? "AI Service Configuration Issue" :
                                                             "AI Service Unavailable"}
                  </div>
                </div>
                <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6, marginBottom: 16 }}>{aiError.message}</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
                  Your transactions and reports are not affected — only the AI advisor is temporarily paused.
                </div>
                <button onClick={() => setAiError(null)}
                  style={{ padding: "9px 20px", background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
                  Dismiss
                </button>
              </div>
            )}
            {aiText && !aiLoading && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                  <div style={{ width: 36, height: 36, background: `linear-gradient(135deg, ${C.accent}, ${C.blue})`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🤖</div>
                  <div><div style={{ fontWeight: 800, fontSize: 15 }}>AI Financial Advisor</div><div style={{ fontSize: 12, color: C.muted }}>Powered by Claude · Based on your live transactions</div></div>
                </div>
                <div style={{ background: C.surface, borderRadius: 12, padding: 20, fontSize: 14, lineHeight: 1.8, color: C.text, whiteSpace: "pre-wrap", border: `1px solid ${C.border}` }}>{aiText}</div>
              </div>
            )}
            {!aiText && !aiLoading && !aiError && (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 60, textAlign: "center" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Ready to analyse</div>
                <div style={{ color: C.muted, fontSize: 14 }}>Click a button above to get personalised advice based on your live data</div>
              </div>
            )}
          </div>
        )}

        {tab === "investments" && (
          <InvestmentsScreen
            monthlyIncome={totalIncome}
            monthlyExpense={totalExpense}
            monthlyEssentialExpense={essentialExpense}
            monthlySavings={savings}
          />
        )}

        {tab === "family" && (
          <FamilyScreen
            transactions={transactions}
            familyMembers={familyMembers}
            editMembers={editMembers}
            setEditMembers={setEditMembers}
            savingFamily={savingFamily}
            onSaveFamily={async () => {
              setSavingFamily(true);
              try { const r = await profileApi.saveFamily(editMembers); setFamilyMembers(r.members); }
              catch (e) { setError(e.message); }
              finally { setSavingFamily(false); }
            }}
            invite={invite} setInvite={setInvite}
            link={link} setLink={setLink}
            inviteEmail={inviteEmail} setInviteEmail={setInviteEmail}
            joinCode={joinCode} setJoinCode={setJoinCode}
            sharingBusy={sharingBusy} setSharingBusy={setSharingBusy}
            setError={setError}
          />
        )}

        {tab === "roadmap" && <RoadmapScreen />}
      </div>

      {/* Bottom navigation — visible on mobile only (CSS hides on desktop) */}
      <nav className="bottom-nav">
        {tabs.map(t => (
          <button key={t.id} className="bottom-nav-item" onClick={() => setTab(t.id)}
            style={{
              color: tab === t.id ? C.text : C.muted,
              borderTop: tab === t.id ? `2px solid ${C.text}` : "2px solid transparent",
              fontWeight: tab === t.id ? 700 : 500,
            }}>
            <span className="bottom-nav-icon"><TabIcon name={t.icon} size={22} /></span>
            <span className="bottom-nav-label">{t.short}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

// Admin panel — separate route, separate Basic Auth, NOT wrapped in Cognito AuthGate.
// Triggered when URL pathname starts with /admin (or query ?admin=1).
const isAdminRoute = () => {
  const p = window.location.pathname || "";
  return p === "/admin" || p.startsWith("/admin/") || window.location.search.includes("admin=1");
};

export default function App() {
  if (isAdminRoute()) {
    // Lazy import keeps the admin bundle out of the main user flow
    const AdminScreen = require("./screens/AdminScreen").default;
    return <AdminScreen />;
  }
  return (
    <AuthGate>
      {({ onSignOut }) => (
        <ReceiptQueueProvider>
          <TrackerApp onSignOut={onSignOut} />
        </ReceiptQueueProvider>
      )}
    </AuthGate>
  );
}
